import React, { useState, useEffect, useCallback } from 'react';
import { auditLogsAPI } from '../lib/api';
import {
  Shield, RefreshCw, Filter, User, Package,
  TrendingUp, FileText, Receipt, LifeBuoy, LogIn, Trash2,
  Edit3, Plus, ChevronDown,
} from 'lucide-react';
import { Skeleton } from '../components/ui/skeleton';

// ─── Labels ───────────────────────────────────────────────────────────────────

const ACTION_META = {
  create: { label: 'Création',     color: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300', icon: Plus },
  update: { label: 'Modification', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',       icon: Edit3 },
  delete: { label: 'Suppression',  color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',           icon: Trash2 },
  login:  { label: 'Connexion',    color: 'bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-300', icon: LogIn },
};

const RESOURCE_META = {
  contact:        { label: 'Contact',        icon: User },
  opportunity:    { label: 'Opportunité',    icon: TrendingUp },
  quote:          { label: 'Devis',          icon: FileText },
  invoice:        { label: 'Facture',        icon: Receipt },
  product:        { label: 'Produit',        icon: Package },
  ticket:         { label: 'Ticket',         icon: LifeBuoy },
  ticket_message: { label: 'Message ticket', icon: LifeBuoy },
  user:           { label: 'Utilisateur',    icon: User },
};

const RESOURCE_TYPES = ['contact', 'opportunity', 'quote', 'invoice', 'product', 'ticket', 'ticket_message', 'user'];
const ACTION_TYPES   = ['create', 'update', 'delete', 'login'];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })
    + ' ' + d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

function ActionBadge({ action }) {
  const meta = ACTION_META[action] || { label: action, color: 'bg-gray-100 text-gray-700', icon: Edit3 };
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${meta.color}`}>
      <Icon size={11} />
      {meta.label}
    </span>
  );
}

function ResourceBadge({ type }) {
  const meta = RESOURCE_META[type] || { label: type, icon: Package };
  const Icon = meta.icon;
  return (
    <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
      <Icon size={12} className="flex-shrink-0" />
      {meta.label}
    </span>
  );
}

function Select({ value, onChange, options, placeholder }) {
  return (
    <div className="relative">
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        className="appearance-none bg-card border border-border rounded-lg pl-3 pr-8 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 cursor-pointer"
      >
        <option value="">{placeholder}</option>
        {options.map(o => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
      <ChevronDown size={14} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function AuditLogPage() {
  const [logs, setLogs]               = useState([]);
  const [loading, setLoading]         = useState(true);
  const [filterAction, setFilterAction]         = useState('');
  const [filterResource, setFilterResource]     = useState('');
  const [expandedId, setExpandedId]   = useState(null);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (filterAction)   params.action        = filterAction;
      if (filterResource) params.resource_type = filterResource;
      const r = await auditLogsAPI.list(params);
      setLogs(r.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [filterAction, filterResource]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const stats = {
    total:   logs.length,
    creates: logs.filter(l => l.action === 'create').length,
    updates: logs.filter(l => l.action === 'update').length,
    deletes: logs.filter(l => l.action === 'delete').length,
    logins:  logs.filter(l => l.action === 'login').length,
  };

  return (
    <div className="space-y-4 animate-fade-in">

      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Shield size={22} className="text-primary" />
            Audit des activités
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Toutes les actions effectuées sur la plateforme
          </p>
        </div>
        <button
          onClick={fetchLogs}
          className="flex items-center gap-1.5 px-3 py-2 text-sm bg-card border border-border rounded-lg hover:bg-muted transition-colors"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          Actualiser
        </button>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 xl:grid-cols-5 gap-3">
        {[
          { label: 'Total actions',  value: stats.total,   color: 'text-foreground' },
          { label: 'Créations',      value: stats.creates, color: 'text-emerald-600 dark:text-emerald-400' },
          { label: 'Modifications',  value: stats.updates, color: 'text-blue-600 dark:text-blue-400' },
          { label: 'Suppressions',   value: stats.deletes, color: 'text-red-600 dark:text-red-400' },
          { label: 'Connexions',     value: stats.logins,  color: 'text-violet-600 dark:text-violet-400' },
        ].map(({ label, value, color }) => (
          <div key={label} className="bg-card border border-border rounded-xl p-4 shadow-sm">
            <p className="text-xs text-muted-foreground font-medium">{label}</p>
            <p className={`text-2xl font-bold mt-1 ${color}`}>{value}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-card border border-border rounded-xl p-4 shadow-sm">
        <div className="flex flex-wrap items-center gap-3">
          <Filter size={15} className="text-muted-foreground flex-shrink-0" />
          <span className="text-sm font-medium text-foreground">Filtres :</span>
          <Select
            value={filterAction}
            onChange={setFilterAction}
            placeholder="Toutes les actions"
            options={ACTION_TYPES.map(a => ({ value: a, label: ACTION_META[a]?.label || a }))}
          />
          <Select
            value={filterResource}
            onChange={setFilterResource}
            placeholder="Toutes les ressources"
            options={RESOURCE_TYPES.map(r => ({ value: r, label: RESOURCE_META[r]?.label || r }))}
          />
          {(filterAction || filterResource) && (
            <button
              onClick={() => { setFilterAction(''); setFilterResource(''); }}
              className="text-xs text-muted-foreground hover:text-foreground underline transition-colors"
            >
              Réinitialiser
            </button>
          )}
        </div>
      </div>

      {/* Log table */}
      <div className="bg-card border border-border rounded-xl shadow-sm overflow-hidden">

        {/* Desktop table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/40">
                <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Date</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Action</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Ressource</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Nom</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Utilisateur</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Détails</th>
              </tr>
            </thead>
            <tbody>
              {loading
                ? Array.from({ length: 8 }).map((_, i) => (
                    <tr key={i} className="border-b border-border last:border-0">
                      {[1,2,3,4,5,6].map(j => (
                        <td key={j} className="px-4 py-3">
                          <Skeleton className="h-4 w-full" />
                        </td>
                      ))}
                    </tr>
                  ))
                : logs.length === 0
                  ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-12 text-center text-muted-foreground text-sm">
                        Aucune activité enregistrée
                      </td>
                    </tr>
                  )
                  : logs.map(log => (
                    <tr key={log.id} className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors">
                      <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">{formatDate(log.created_at)}</td>
                      <td className="px-4 py-3"><ActionBadge action={log.action} /></td>
                      <td className="px-4 py-3"><ResourceBadge type={log.resource_type} /></td>
                      <td className="px-4 py-3 font-medium text-foreground max-w-[180px] truncate">{log.resource_name || '—'}</td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col">
                          <span className="text-xs font-medium text-foreground leading-tight">{log.user_name || '—'}</span>
                          <span className="text-[10px] text-muted-foreground">{log.user_email || ''}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs text-muted-foreground font-mono max-w-[200px] truncate">
                        {log.details || '—'}
                      </td>
                    </tr>
                  ))
              }
            </tbody>
          </table>
        </div>

        {/* Mobile cards */}
        <div className="md:hidden divide-y divide-border">
          {loading
            ? Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="p-4 space-y-2">
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-3 w-1/2" />
                </div>
              ))
            : logs.length === 0
              ? <p className="p-8 text-center text-sm text-muted-foreground">Aucune activité enregistrée</p>
              : logs.map(log => (
                <div
                  key={log.id}
                  className="p-4 cursor-pointer hover:bg-muted/30 transition-colors"
                  onClick={() => setExpandedId(expandedId === log.id ? null : log.id)}
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <ActionBadge action={log.action} />
                        <ResourceBadge type={log.resource_type} />
                      </div>
                      <p className="text-sm font-medium text-foreground mt-1 truncate">{log.resource_name || '—'}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{log.user_name} · {formatDate(log.created_at)}</p>
                    </div>
                    <ChevronDown
                      size={16}
                      className={`text-muted-foreground flex-shrink-0 transition-transform ${expandedId === log.id ? 'rotate-180' : ''}`}
                    />
                  </div>
                  {expandedId === log.id && log.details && (
                    <div className="mt-2 pt-2 border-t border-border">
                      <p className="text-xs text-muted-foreground font-mono break-all">{log.details}</p>
                    </div>
                  )}
                </div>
              ))
          }
        </div>
      </div>

      {!loading && logs.length > 0 && (
        <p className="text-xs text-center text-muted-foreground pb-2">
          {logs.length} entrée{logs.length > 1 ? 's' : ''} affichée{logs.length > 1 ? 's' : ''}
        </p>
      )}
    </div>
  );
}
